import { CollisionDetection, DragEndEvent, DragMoveEvent, DragStartEvent, Modifier, rectIntersection } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import * as monaco from 'monaco-editor';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/ReduxHooks';
import type {
  CodeSnippetCodeByDropZone,
  CodeSnippetDropZone,
  CodeSnippetDropZoneMode,
  CodeSnippetImport,
  dropInfo,
} from '../../Components/CodeEditor/types';
import {
  insertPreviewSnippet,
  insertSnippetAtLine,
  isValidSnippetDropZone,
  resolveSnippetCodeForTarget,
  uniquifyVariableName,
} from '../../Components/CodeEditor/utils/snippetInsertion';
import type { PendingActionDrop } from '../../Components/CodeEditor/components/ActionArgumentsDialog';
import type { ActionItem } from '../../redux/EditorSlice';
import { findActionDefForLabel } from '../../Components/CodeEditor/ConfigurationTabs/driveModeAutonomousActionUtils';
import type { PendingDeviceSnippetPortDrop } from '../../utils/deviceSnippetPortDrop';
import { parseDeviceSnippetPortDrop } from '../../utils/deviceSnippetPortDrop';
import { hasDriveBaseDefinition, isDriveBaseConstructorSnippet } from '../../Components/CodeEditor/utils/driveBaseSingleton';

const FOLLOW_PATH_ACTION_CALL_RE = /\bfollow_path_action\s*\(/;
const EMPTY_CALL_RE = /\b[A-Za-z_]\w*(?:\.[A-Za-z_]\w*)*\(\s*\)/;

interface UseEditorDragDropParams {
  standaloneEditorRef: MutableRefObject<monaco.editor.IStandaloneCodeEditor | null>;
  previewModelRef: MutableRefObject<monaco.editor.ITextModel | null>;
  debouncedSync: (code: string) => void;
  onPathDropRequested: (code: string, targetLine: number) => void;
  onNoPathsAvailable: () => void;
  onActionArgsDropRequested: (drop: PendingActionDrop) => void;
  onDeviceSnippetPortDropRequested: (drop: PendingDeviceSnippetPortDrop & { targetLine: number }) => void;
  onDuplicateDriveBaseDropRequested: () => void;
}

export function useEditorDragDrop({
  standaloneEditorRef,
  previewModelRef,
  debouncedSync,
  onPathDropRequested,
  onNoPathsAvailable,
  onActionArgsDropRequested,
  onDeviceSnippetPortDropRequested,
  onDuplicateDriveBaseDropRequested,
}: UseEditorDragDropParams) {
  const project = useAppSelector((state) => state.project.loadedProject);
  const globalActionList = useAppSelector((state) => state.editor.actionList) as ActionItem[];

  const [dropInfo, setDropInfo] = useState<dropInfo>({ dropObject: { id: '', code: '', type: '' }, cursorPos: null });
  const [isDragging, setIsDragging] = useState(false);

  const draggingDecorationRef = useRef<monaco.editor.IEditorDecorationsCollection | null>();
  const lastPreviewRef = useRef<{ line: number; numLines?: number; isValidZone?: boolean; resolvedDropZone?: CodeSnippetDropZone | null } | null>(
    null
  );
  const currentCursorPosition = useRef({ x: 0, y: 0 });
  const originalModelRef = useRef<monaco.editor.ITextModel | null>(null);

  const setModelPreservingViewState = (editor: monaco.editor.IStandaloneCodeEditor, model: monaco.editor.ITextModel) => {
    if (editor.getModel() === model) return;
    const viewState = editor.saveViewState();
    try {
      editor.setModel(model);
      if (viewState) {
        editor.restoreViewState(viewState);
      }
    } catch {
      // Ignore swap errors
    }
  };

  const clearPreviewState = (editor?: monaco.editor.IStandaloneCodeEditor | null, restoreOriginalModel = false) => {
    if (restoreOriginalModel && editor && originalModelRef.current) {
      setModelPreservingViewState(editor, originalModelRef.current);
    }

    draggingDecorationRef.current?.clear();
    lastPreviewRef.current = null;
    previewModelRef.current?.dispose();
    previewModelRef.current = null;
    originalModelRef.current = null;
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent | PointerEvent) => {
      currentCursorPosition.current = { x: e.clientX, y: e.clientY };
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('pointermove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('pointermove', handleMouseMove);
    };
  }, [isDragging, standaloneEditorRef]);

  const SCROLL_MARGIN = 80;
  const SCROLL_STEP = 16;
  const SCROLL_INTERVAL_MS = 50;

  useEffect(() => {
    if (!isDragging) return;

    const tick = () => {
      const editor = standaloneEditorRef.current;
      if (!editor) return;

      const { y: cursorY } = currentCursorPosition.current;
      const domNode = editor.getContainerDomNode();
      if (!domNode) return;

      const rect = domNode.getBoundingClientRect();
      if (cursorY < rect.top || cursorY > rect.bottom) return;
      const inTopZone = cursorY < rect.top + SCROLL_MARGIN;
      const inBottomZone = cursorY > rect.bottom - SCROLL_MARGIN;

      if (!inTopZone && !inBottomZone) return;

      const scrollTop = editor.getScrollTop();
      const scrollHeight = editor.getScrollHeight();
      const viewHeight = domNode.clientHeight;
      const maxScrollTop = Math.max(0, scrollHeight - viewHeight);

      if (inTopZone && scrollTop > 0) {
        editor.setScrollTop(Math.max(0, scrollTop - SCROLL_STEP));
      } else if (inBottomZone && scrollTop < maxScrollTop) {
        editor.setScrollTop(Math.min(maxScrollTop, scrollTop + SCROLL_STEP));
      }
    };

    const id = setInterval(tick, SCROLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isDragging, standaloneEditorRef]);

  const handleDragStart = (event: DragStartEvent) => {
    const activatorCoordinates = getEventCoordinates(event.activatorEvent);
    if (activatorCoordinates) {
      currentCursorPosition.current = {
        x: activatorCoordinates.x,
        y: activatorCoordinates.y,
      };
    }

    clearPreviewState(standaloneEditorRef.current, false);
    setIsDragging(true);
    setDropInfo({
      dropObject: {
        id: event.active.id as string,
        code: event.active.data.current?.code as string,
        insertText: event.active.data.current?.insertText as string | undefined,
        type: event.active.data.current?.type as string,
        dropZone: event.active.data.current?.dropZone as CodeSnippetDropZoneMode | undefined,
        codeByDropZone: event.active.data.current?.codeByDropZone as CodeSnippetCodeByDropZone | undefined,
        imports: event.active.data.current?.imports as CodeSnippetImport[] | undefined,
        receiverClassName: event.active.data.current?.receiverClassName as string | undefined,
        frameworkClassName: event.active.data.current?.frameworkClassName as string | undefined,
      },
      cursorPos: null,
    });
  };

  const handleDragCancel = () => {
    document.body.style.cursor = 'default';
    setIsDragging(false);
    clearPreviewState(standaloneEditorRef.current, true);
  };

  const handleDragging = (event: DragMoveEvent) => {
    const editor = standaloneEditorRef.current;
    if (!editor) return;

    if (event.over?.id !== 'editor') {
      document.body.style.cursor = 'not-allowed';
      if (originalModelRef.current) setModelPreservingViewState(editor, originalModelRef.current);
    } else {
      document.body.style.cursor = 'copy';

      const { x, y } = currentCursorPosition.current;
      const mouseEvent = editor.getTargetAtClientPoint(x - 70, y);
      const cursorPos: monaco.Position | null | undefined = mouseEvent?.position;

      if (cursorPos) {
        const currentModel = editor.getModel();
        if (!originalModelRef.current && currentModel) {
          originalModelRef.current = currentModel;
        }

        if (!previewModelRef.current && currentModel) {
          previewModelRef.current = monaco.editor.createModel(currentModel.getValue(), 'python');
        }

        if (!previewModelRef.current) return;

        if (editor.getModel() !== previewModelRef.current) {
          setModelPreservingViewState(editor, previewModelRef.current);
        }

        const modelForUniquify = originalModelRef.current ?? currentModel;
        const snippetType = event.active.data.current?.type as string | undefined;
        const snippetDropZone = event.active.data.current?.dropZone as CodeSnippetDropZoneMode | undefined;
        const receiverClassName = event.active.data.current?.receiverClassName as string | undefined;
        const frameworkClassName = event.active.data.current?.frameworkClassName as string | undefined;
        let targetLineForResolution = cursorPos.lineNumber;
        const previousPreview = lastPreviewRef.current;
        if (previousPreview && editor.getModel() === previewModelRef.current && previousPreview.line < targetLineForResolution) {
          targetLineForResolution -= previousPreview.numLines ?? 1;
        }
        if (modelForUniquify) {
          targetLineForResolution = Math.min(Math.max(targetLineForResolution, 1), modelForUniquify.getLineCount());
        }

        const resolvedSnippet = resolveSnippetCodeForTarget({
          code: dropInfo.dropObject.code,
          codeByDropZone: event.active.data.current?.codeByDropZone as CodeSnippetCodeByDropZone | undefined,
          model: modelForUniquify,
          targetLine: targetLineForResolution,
          snippetType,
          dropZone: snippetDropZone,
          receiverClassName,
          frameworkClassName,
          uniquifyTopLevelSetup: true,
        });
        insertPreviewSnippet({
          editor,
          textToInsert: resolvedSnippet.code ?? '',
          cursorPos,
          lastPreviewRef,
          draggingDecorationRef,
          snippetType,
          dropZone: snippetDropZone,
          codeByDropZone: resolvedSnippet.codeByDropZone,
          frameworkClassName,
        });
        document.body.style.cursor = lastPreviewRef.current?.isValidZone === false ? 'not-allowed' : 'copy';
      }
    }
  };

  const handleDrop = async (event: DragEndEvent) => {
    document.body.style.cursor = 'default';
    setIsDragging(false);
    draggingDecorationRef.current?.clear();
    const editor = standaloneEditorRef.current;

    if (!editor || event.over?.id !== 'editor') {
      clearPreviewState(editor, true);
      return;
    }

    const droppedCode: string | undefined = event.active.data.current?.code as string | undefined;
    const droppedInsertText: string | undefined = event.active.data.current?.insertText as string | undefined;
    const snippetType = event.active.data.current?.type as string | undefined;
    const snippetDropZone = event.active.data.current?.dropZone as CodeSnippetDropZoneMode | undefined;
    const codeByDropZone = event.active.data.current?.codeByDropZone as CodeSnippetCodeByDropZone | undefined;
    const receiverClassName = event.active.data.current?.receiverClassName as string | undefined;
    const frameworkClassName = event.active.data.current?.frameworkClassName as string | undefined;

    const getTargetLine = (): number => {
      if (lastPreviewRef.current?.line) return lastPreviewRef.current.line;
      const { x, y } = currentCursorPosition.current;
      const hit = editor.getTargetAtClientPoint(x - 70, y);
      return hit?.position?.lineNumber || editor.getPosition()?.lineNumber || 1;
    };

    const targetLine = getTargetLine();
    const validationModel = originalModelRef.current ?? editor.getModel();
    const isValidDropZone =
      lastPreviewRef.current?.isValidZone ??
      (validationModel ? isValidSnippetDropZone(validationModel, targetLine, snippetType, snippetDropZone, droppedCode, frameworkClassName) : false);
    if (!isValidDropZone) {
      if (validationModel && isDriveBaseConstructorSnippet(droppedCode, frameworkClassName) && hasDriveBaseDefinition(validationModel.getValue())) {
        onDuplicateDriveBaseDropRequested();
      }
      clearPreviewState(editor, true);
      return;
    }
    const resolvedSnippet = resolveSnippetCodeForTarget({
      code: droppedCode,
      codeByDropZone,
      model: validationModel,
      targetLine,
      snippetType,
      dropZone: snippetDropZone,
      receiverClassName,
      frameworkClassName,
      resolvedDropZone: lastPreviewRef.current?.resolvedDropZone,
    });
    const resolvedDroppedCode = resolvedSnippet.codeForTarget;

    const needsPath =
      typeof resolvedDroppedCode === 'string' &&
      FOLLOW_PATH_ACTION_CALL_RE.test(resolvedDroppedCode) &&
      (/\bpath\s*=\s*(?=[,)]|$)/.test(resolvedDroppedCode) || /\bname\s*=\s*(?=[,)]|$)/.test(resolvedDroppedCode));
    if (needsPath && resolvedDroppedCode) {
      if ((project?.paths || []).length === 0) {
        clearPreviewState(editor, true);
        onNoPathsAvailable();
        return;
      }
      clearPreviewState(editor, true);
      onPathDropRequested(resolvedDroppedCode, targetLine);
      return;
    }

    const devicePortParsed = resolvedDroppedCode ? parseDeviceSnippetPortDrop(resolvedDroppedCode, snippetType) : null;
    if (devicePortParsed) {
      clearPreviewState(editor, true);
      onDeviceSnippetPortDropRequested({
        ...devicePortParsed,
        targetLine,
        imports: event.active.data.current?.imports as CodeSnippetImport[] | undefined,
      });
      return;
    }

    if (typeof resolvedDroppedCode === 'string' && !FOLLOW_PATH_ACTION_CALL_RE.test(resolvedDroppedCode)) {
      const actionMatch = resolvedDroppedCode.match(/^([A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*)\s*\(/);
      const actionLabel = actionMatch?.[1] || '';
      const actionDef = actionLabel ? findActionDefForLabel(globalActionList, actionLabel) : null;

      if (actionDef?.arguments && actionDef.arguments.length > 0) {
        clearPreviewState(editor, true);
        onActionArgsDropRequested({ actionLabel, code: resolvedDroppedCode, targetLine, actionDef });
        return;
      }
    }

    const committedModel = originalModelRef.current ?? editor.getModel();

    try {
      if (committedModel) {
        setModelPreservingViewState(editor, committedModel);
      }

      if (resolvedDroppedCode && committedModel) {
        const codeToInsert = uniquifyVariableName(resolvedDroppedCode, committedModel);
        insertSnippetAtLine({
          editor,
          textToInsert: codeToInsert,
          snippetTextToInsert: droppedInsertText,
          triggerParameterHints: Boolean(droppedInsertText) || EMPTY_CALL_RE.test(codeToInsert),
          targetLine,
          imports: event.active.data.current?.imports as CodeSnippetImport[] | undefined,
        });
        const newCode = editor.getModel()?.getValue();
        if (newCode) debouncedSync(newCode);
      }
    } finally {
      clearPreviewState(editor, false);
    }
  };

  const snapLeftToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
    if (draggingNodeRect && activatorEvent) {
      const activatorCoordinates = getEventCoordinates(activatorEvent);

      if (!activatorCoordinates) {
        return transform;
      }

      const offsetX = activatorCoordinates.x - draggingNodeRect.left;
      const offsetY = activatorCoordinates.y - draggingNodeRect.top;

      return {
        ...transform,
        x: transform.x + offsetX - 5,
        y: transform.y + offsetY - draggingNodeRect.height / 2,
      };
    }

    return transform;
  };

  const fixCursorSnapOffset: CollisionDetection = (args) => {
    if (!args.pointerCoordinates) {
      return rectIntersection(args);
    }
    const { x, y } = args.pointerCoordinates;
    const { width, height } = args.collisionRect;
    const updated = {
      ...args,
      collisionRect: {
        width,
        height,
        bottom: y + height,
        left: x - width,
        right: x + width,
        top: y - height,
      },
    };
    return rectIntersection(updated);
  };

  return {
    isDragging,
    dropInfo,
    handleDragStart,
    handleDragging,
    handleDrop,
    handleDragCancel,
    snapLeftToCursor,
    fixCursorSnapOffset,
  };
}
